import type { DocumentIndexingStatus } from "@/services/documentService";
import type { CNSSourceImportBatchStatus } from "@/models/sourceConnection";
import { SOURCE_TYPES, type SourceType } from "@/types/sourceTypes";
import type {
  CNSSourceCredentialStatus,
  CNSSourceEffectiveStatus,
} from "@/models/sourceConnection";
import type { ConnectorOAuthAttemptStatus } from "@/models/connectorOAuth";
import type {
  CNSConversationSuggestionsChangedEvent,
  CNSDocumentChangedEvent,
} from "@/models/generated/asyncapi-events";
import {
  parseConversationSuggestions,
  type ConversationSuggestions,
} from "@/lib/suggestions/conversationSuggestionContract";

export type DocumentChangedWireEvent = Omit<CNSDocumentChangedEvent, "name">;

export interface DocumentIndexingStatusChangedWireEvent {
  documentPublicId: string;
  projectPublicId: string;
  indexingStatus: DocumentIndexingStatus;
  indexingError?: string | null;
}

export type LineageEdgeChangedWireAction = "ATTACHED" | "DETACHED";

export interface LineageEdgeChangedWireEvent {
  edgePublicId: string;
  projectPublicId: string;
  action: LineageEdgeChangedWireAction;
  subjectType: SourceType;
  subjectPublicId: string;
  targetType: string;
  targetPublicId: string;
}

interface NormalizedDocumentChangedPayload {
  name: "documentChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  event: DocumentChangedWireEvent;
}

interface NormalizedDocumentIndexingPayload {
  name: "documentIndexingStatusChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  event: DocumentIndexingStatusChangedWireEvent;
}

interface NormalizedLineageEdgeChangedPayload {
  name: "lineageEdgeChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  event: LineageEdgeChangedWireEvent;
}

export interface SourceConnectionStatusChangedWireEvent {
  workspacePublicId: string;
  projectPublicId?: string;
  conversationPublicId?: string;
  credentialPublicId?: string;
  connectionPublicId?: string;
  attemptPublicId?: string;
  toolCallId?: string;
  credentialStatus?: CNSSourceCredentialStatus;
  effectiveStatus?: CNSSourceEffectiveStatus;
  attemptStatus?: ConnectorOAuthAttemptStatus;
  enabled?: boolean;
  displayAccount?: string | null;
  errorCode?: string | null;
  toolResultApplied?: boolean;
  continuationClaimedAt?: string | null;
  claimedAssistantMessageId?: string | null;
}

interface NormalizedSourceConnectionStatusChangedPayload {
  name: "sourceConnectionStatusChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  workspacePublicId: string;
  projectPublicId?: string;
  conversationPublicId?: string;
  event: SourceConnectionStatusChangedWireEvent;
}

export interface SourceImportBatchChangedWireEvent {
  batchPublicId: string;
  projectPublicId: string;
  connectionPublicId: string;
  conversationPublicId?: string | null;
  status: CNSSourceImportBatchStatus;
  totalCount: number;
  completedCount: number;
}

interface NormalizedSourceImportBatchChangedPayload {
  name: "sourceImportBatchChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  conversationPublicId?: string;
  event: SourceImportBatchChangedWireEvent;
}

export interface ToolResultAppliedWireEvent {
  entityHolderPublicId: string;
  conversationPublicId: string;
  toolCallId: string;
  toolName: string;
}

interface NormalizedToolResultAppliedPayload {
  name: "toolResultAppliedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  conversationPublicId?: string;
  event: ToolResultAppliedWireEvent;
}

export type ConversationMessageChangeKind = "PROGRESS" | "COMPLETED" | "EDITED";

export interface ConversationMessageChangedWireEvent {
  conversationPublicId: string;
  messagePublicId: string;
  changeKind: ConversationMessageChangeKind;
}

interface NormalizedConversationMessageChangedPayload {
  name: "conversationMessageChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  conversationPublicId?: string;
  event: ConversationMessageChangedWireEvent;
}

export type ConversationSuggestionsChangedWireEvent = Omit<
  CNSConversationSuggestionsChangedEvent,
  "name" | "suggestions"
> & {
  /** Runtime validation narrows the generated numeric version to the one supported client version. */
  suggestions: ConversationSuggestions;
};

interface NormalizedConversationSuggestionsChangedPayload {
  name: "conversationSuggestionsChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId: string;
  workspacePublicId: string;
  projectPublicId: string;
  conversationPublicId: string;
  event: ConversationSuggestionsChangedWireEvent;
}

export interface AssumptionChangedWireEvent {
  assumptionPublicId: string;
  projectPublicId: string;
  conversationPublicId?: string | null;
}

interface NormalizedAssumptionChangedPayload {
  name: "assumptionChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  conversationPublicId?: string;
  event: AssumptionChangedWireEvent;
}

export interface HypothesisChangedWireEvent {
  hypothesisPublicId: string;
  projectPublicId: string;
  conversationPublicId?: string | null;
}

interface NormalizedHypothesisChangedPayload {
  name: "hypothesisChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  conversationPublicId?: string;
  event: HypothesisChangedWireEvent;
}

export interface ExperimentChangedWireEvent {
  experimentPublicId: string;
  projectPublicId: string;
  conversationPublicId?: string | null;
}

interface NormalizedExperimentChangedPayload {
  name: "experimentChangedEvent";
  entityType: string;
  entityPublicId: string;
  userPublicId?: string;
  projectPublicId: string;
  conversationPublicId?: string;
  event: ExperimentChangedWireEvent;
}

export type NormalizedResourceEventPayload =
  | NormalizedDocumentChangedPayload
  | NormalizedDocumentIndexingPayload
  | NormalizedLineageEdgeChangedPayload
  | NormalizedSourceConnectionStatusChangedPayload
  | NormalizedSourceImportBatchChangedPayload
  | NormalizedToolResultAppliedPayload
  | NormalizedConversationMessageChangedPayload
  | NormalizedConversationSuggestionsChangedPayload
  | NormalizedAssumptionChangedPayload
  | NormalizedHypothesisChangedPayload
  | NormalizedExperimentChangedPayload;

const INDEXING_STATUSES: DocumentIndexingStatus[] = ["PENDING", "INDEXED", "FAILED"];
const LINEAGE_ACTIONS: LineageEdgeChangedWireAction[] = ["ATTACHED", "DETACHED"];
const CONVERSATION_MESSAGE_CHANGE_KINDS: ConversationMessageChangeKind[] = [
  "PROGRESS",
  "COMPLETED",
  "EDITED",
];
const SOURCE_CREDENTIAL_STATUSES: CNSSourceCredentialStatus[] = [
  "pending",
  "connected",
  "error",
  "revoked",
];
const SOURCE_EFFECTIVE_STATUSES: CNSSourceEffectiveStatus[] = [
  "pending",
  "connected",
  "error",
  "revoked",
  "disabled",
];
const OAUTH_ATTEMPT_STATUSES: ConnectorOAuthAttemptStatus[] = [
  "preparing",
  "pending",
  "exchanging",
  "succeeded",
  "failed",
  "cancelled",
  "expired",
];
const IMPORT_BATCH_STATUSES: CNSSourceImportBatchStatus[] = [
  "QUEUED",
  "RUNNING",
  "CANCELLING",
  "PARTIAL",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED",
];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

function asIndexingStatus(value: unknown): DocumentIndexingStatus | undefined {
  return typeof value === "string" && INDEXING_STATUSES.includes(value as DocumentIndexingStatus)
    ? (value as DocumentIndexingStatus)
    : undefined;
}

function asLineageAction(value: unknown): LineageEdgeChangedWireAction | undefined {
  return typeof value === "string" && LINEAGE_ACTIONS.includes(value as LineageEdgeChangedWireAction)
    ? (value as LineageEdgeChangedWireAction)
    : undefined;
}

function asSourceType(value: unknown): SourceType | undefined {
  return typeof value === "string" && SOURCE_TYPES.includes(value as SourceType)
    ? (value as SourceType)
    : undefined;
}

function asCredentialStatus(value: unknown): CNSSourceCredentialStatus | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.toLowerCase() as CNSSourceCredentialStatus;
  return SOURCE_CREDENTIAL_STATUSES.includes(normalized) ? normalized : undefined;
}

function asEffectiveStatus(value: unknown): CNSSourceEffectiveStatus | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.toLowerCase() as CNSSourceEffectiveStatus;
  return SOURCE_EFFECTIVE_STATUSES.includes(normalized) ? normalized : undefined;
}

function asAttemptStatus(value: unknown): ConnectorOAuthAttemptStatus | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.toLowerCase() as ConnectorOAuthAttemptStatus;
  return OAUTH_ATTEMPT_STATUSES.includes(normalized) ? normalized : undefined;
}

function asImportBatchStatus(value: unknown): CNSSourceImportBatchStatus | undefined {
  return typeof value === "string" && IMPORT_BATCH_STATUSES.includes(value as CNSSourceImportBatchStatus)
    ? (value as CNSSourceImportBatchStatus)
    : undefined;
}

function asCount(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function credentialStatusFromEffective(
  status: CNSSourceEffectiveStatus | undefined,
): CNSSourceCredentialStatus | undefined {
  return status && status !== "disabled" ? status : undefined;
}

/**
 * Validates and normalizes a resource-events STOMP frame to the AsyncAPI envelope shape.
 */
export function normalizeResourceEventPayload(
  value: unknown
): NormalizedResourceEventPayload | null {
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const name = asString(record.name);
  const entityType = asString(record.entityType);
  const entityPublicId = asString(record.entityPublicId);
  const projectPublicId = asString(record.projectPublicId);
  const eventRecord = asRecord(record.event);

  if (!name || !entityType || !entityPublicId || !eventRecord) {
    return null;
  }

  if (name === "conversationSuggestionsChangedEvent") {
    const suggestions = parseConversationSuggestions(eventRecord.suggestions);
    const workspacePublicId = asString(record.workspacePublicId);
    const userPublicId = asString(record.userPublicId);
    const eventProjectPublicId = projectPublicId ?? suggestions?.projectId;
    const conversationPublicId =
      asString(record.conversationPublicId) ?? suggestions?.conversationId;
    if (
      !suggestions ||
      !userPublicId ||
      !workspacePublicId ||
      !eventProjectPublicId ||
      !conversationPublicId ||
      suggestions.id !== entityPublicId ||
      suggestions.actorId !== userPublicId ||
      suggestions.workspaceId !== workspacePublicId ||
      suggestions.projectId !== eventProjectPublicId ||
      suggestions.conversationId !== conversationPublicId
    ) {
      return null;
    }
    return {
      name,
      entityType,
      entityPublicId,
      userPublicId,
      workspacePublicId,
      projectPublicId: eventProjectPublicId,
      conversationPublicId,
      event: { suggestions },
    };
  }

  if (name === "documentChangedEvent") {
    const documentPublicId = asString(eventRecord.documentPublicId) ?? entityPublicId;
    const eventProjectPublicId = asString(eventRecord.projectPublicId) ?? projectPublicId;
    const documentName = asString(eventRecord.documentName);
    const documentSize = asCount(eventRecord.documentSize);
    const documentType = asString(eventRecord.documentType);
    const indexingStatus = asIndexingStatus(eventRecord.indexingStatus);
    if (
      !documentPublicId ||
      !eventProjectPublicId ||
      !documentName ||
      documentSize === undefined ||
      documentSize < 0 ||
      !documentType ||
      !indexingStatus
    ) {
      return null;
    }

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      event: {
        documentPublicId,
        projectPublicId: eventProjectPublicId,
        documentName,
        documentSize,
        documentType,
        indexingStatus,
      },
    };
  }

  if (name === "documentIndexingStatusChangedEvent") {
    const documentPublicId = asString(eventRecord.documentPublicId) ?? entityPublicId;
    const eventProjectPublicId = asString(eventRecord.projectPublicId) ?? projectPublicId;
    const indexingStatus = asIndexingStatus(eventRecord.indexingStatus);
    if (!documentPublicId || !eventProjectPublicId || !indexingStatus) {
      return null;
    }

    const indexingError = eventRecord.indexingError;
    const normalizedError =
      indexingError === null
        ? null
        : typeof indexingError === "string"
          ? indexingError
          : undefined;

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      event: {
        documentPublicId,
        projectPublicId: eventProjectPublicId,
        indexingStatus,
        indexingError: normalizedError,
      },
    };
  }

  if (name === "lineageEdgeChangedEvent") {
    const edgePublicId = asString(eventRecord.edgePublicId) ?? entityPublicId;
    const eventProjectPublicId = asString(eventRecord.projectPublicId) ?? projectPublicId;
    const action = asLineageAction(eventRecord.action);
    const subjectType = asSourceType(eventRecord.subjectType);
    const subjectPublicId = asString(eventRecord.subjectPublicId);
    const targetType = asString(eventRecord.targetType);
    const targetPublicId = asString(eventRecord.targetPublicId);
    if (
      !edgePublicId ||
      !eventProjectPublicId ||
      !action ||
      !subjectType ||
      !subjectPublicId ||
      !targetType ||
      !targetPublicId
    ) {
      return null;
    }

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      event: {
        edgePublicId,
        projectPublicId: eventProjectPublicId,
        action,
        subjectType,
        subjectPublicId,
        targetType,
        targetPublicId,
      },
    };
  }

  if (name === "sourceConnectionStatusChangedEvent") {
    const workspacePublicId =
      asString(eventRecord.workspacePublicId) ??
      asString(eventRecord.workspaceId) ??
      asString(record.workspacePublicId) ??
      asString(record.workspaceId);
    if (!workspacePublicId) return null;

    const eventProjectPublicId =
      asString(eventRecord.projectPublicId) ??
      asString(eventRecord.projectId) ??
      projectPublicId;
    const conversationPublicId =
      asString(eventRecord.conversationPublicId) ??
      asString(eventRecord.conversationId) ??
      asString(record.conversationPublicId) ??
      asString(record.conversationId);
    const attemptPublicId =
      asString(eventRecord.attemptPublicId) ??
      asString(eventRecord.sourceOAuthAttemptPublicId) ??
      asString(eventRecord.sourceOAuthAttemptId) ??
      asString(record.attemptPublicId) ??
      asString(record.sourceOAuthAttemptPublicId) ??
      asString(record.sourceOAuthAttemptId) ??
      (entityType.toLowerCase().includes("oauth") &&
      entityType.toLowerCase().includes("attempt")
        ? entityPublicId
        : undefined);
    const displayAccount = eventRecord.displayAccount;
    const errorCode = eventRecord.errorCode ?? eventRecord.code;
    const continuationClaimedAt = eventRecord.continuationClaimedAt;
    const claimedAssistantMessageId = eventRecord.claimedAssistantMessageId;
    const effectiveStatus =
      asEffectiveStatus(eventRecord.effectiveStatus) ??
      asEffectiveStatus(eventRecord.connectionStatus);
    const credentialStatus =
      asCredentialStatus(eventRecord.credentialStatus) ??
      credentialStatusFromEffective(effectiveStatus);

    const event: SourceConnectionStatusChangedWireEvent = {
      workspacePublicId,
      projectPublicId: eventProjectPublicId,
      conversationPublicId,
      credentialPublicId:
        asString(eventRecord.credentialPublicId) ??
        asString(eventRecord.sourceCredentialPublicId) ??
        asString(record.credentialPublicId) ??
        (entityType.toLowerCase().includes("credential")
          ? entityPublicId
          : undefined),
      connectionPublicId:
        asString(eventRecord.connectionPublicId) ??
        asString(eventRecord.sourceConnectionPublicId) ??
        asString(record.connectionPublicId) ??
        (entityType.toLowerCase().includes("connection")
          ? entityPublicId
          : undefined),
      attemptPublicId,
      toolCallId:
        asString(eventRecord.toolCallId) ?? asString(record.toolCallId),
      credentialStatus,
      effectiveStatus,
      attemptStatus:
        asAttemptStatus(eventRecord.attemptStatus) ??
        asAttemptStatus(eventRecord.oauthAttemptStatus),
      enabled:
        typeof eventRecord.enabled === "boolean"
          ? eventRecord.enabled
          : undefined,
      displayAccount:
        displayAccount === null
          ? null
          : typeof displayAccount === "string"
            ? displayAccount
            : undefined,
      errorCode:
        errorCode === null
          ? null
          : typeof errorCode === "string"
            ? errorCode
            : undefined,
      toolResultApplied:
        typeof eventRecord.toolResultApplied === "boolean"
          ? eventRecord.toolResultApplied
          : undefined,
      continuationClaimedAt:
        continuationClaimedAt === null
          ? null
          : typeof continuationClaimedAt === "string"
            ? continuationClaimedAt
            : undefined,
      claimedAssistantMessageId:
        claimedAssistantMessageId === null
          ? null
          : typeof claimedAssistantMessageId === "string"
            ? claimedAssistantMessageId
            : undefined,
    };

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      workspacePublicId,
      projectPublicId: eventProjectPublicId,
      conversationPublicId,
      event,
    };
  }

  if (name === "sourceImportBatchChangedEvent") {
    const batchPublicId = asString(eventRecord.batchPublicId) ?? entityPublicId;
    const eventProjectPublicId = asString(eventRecord.projectPublicId) ?? projectPublicId;
    const connectionPublicId = asString(eventRecord.connectionPublicId);
    const status = asImportBatchStatus(eventRecord.status);
    const totalCount = asCount(eventRecord.totalCount);
    const completedCount = asCount(eventRecord.completedCount);
    if (
      !batchPublicId ||
      !eventProjectPublicId ||
      !connectionPublicId ||
      !status ||
      totalCount === undefined ||
      completedCount === undefined
    ) {
      return null;
    }

    const conversationPublicId =
      asString(eventRecord.conversationPublicId) ??
      asString(record.conversationPublicId);

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      conversationPublicId,
      event: {
        batchPublicId,
        projectPublicId: eventProjectPublicId,
        connectionPublicId,
        conversationPublicId,
        status,
        totalCount,
        completedCount,
      },
    };
  }

  if (name === "toolResultAppliedEvent") {
    const entityHolderPublicId =
      asString(eventRecord.entityHolderPublicId) ?? entityPublicId;
    const eventConversationPublicId =
      asString(eventRecord.conversationPublicId) ??
      asString(record.conversationPublicId);
    const toolCallId = asString(eventRecord.toolCallId);
    const toolName = asString(eventRecord.toolName);
    const eventProjectPublicId = projectPublicId;
    if (
      !entityHolderPublicId ||
      !eventConversationPublicId ||
      !toolCallId ||
      !toolName ||
      !eventProjectPublicId
    ) {
      return null;
    }

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      conversationPublicId: eventConversationPublicId,
      event: {
        entityHolderPublicId,
        conversationPublicId: eventConversationPublicId,
        toolCallId,
        toolName,
      },
    };
  }

  if (name === "conversationMessageChangedEvent") {
    const eventConversationPublicId =
      asString(eventRecord.conversationPublicId) ??
      asString(record.conversationPublicId);
    const messagePublicId =
      asString(eventRecord.messagePublicId) ?? entityPublicId;
    const changeKind = asConversationMessageChangeKind(eventRecord.changeKind);
    const eventProjectPublicId = projectPublicId;
    if (
      !eventConversationPublicId ||
      !messagePublicId ||
      !changeKind ||
      !eventProjectPublicId
    ) {
      return null;
    }

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      conversationPublicId: eventConversationPublicId,
      event: {
        conversationPublicId: eventConversationPublicId,
        messagePublicId,
        changeKind,
      },
    };
  }

  if (name === "assumptionChangedEvent") {
    const assumptionPublicId =
      asString(eventRecord.assumptionPublicId) ?? entityPublicId;
    const eventProjectPublicId =
      asString(eventRecord.projectPublicId) ?? projectPublicId;
    if (!assumptionPublicId || !eventProjectPublicId) {
      return null;
    }
    const conversationPublicId =
      asString(eventRecord.conversationPublicId) ??
      asString(record.conversationPublicId) ??
      null;

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      conversationPublicId: conversationPublicId ?? undefined,
      event: {
        assumptionPublicId,
        projectPublicId: eventProjectPublicId,
        conversationPublicId,
      },
    };
  }

  if (name === "hypothesisChangedEvent") {
    const hypothesisPublicId =
      asString(eventRecord.hypothesisPublicId) ?? entityPublicId;
    const eventProjectPublicId =
      asString(eventRecord.projectPublicId) ?? projectPublicId;
    if (!hypothesisPublicId || !eventProjectPublicId) {
      return null;
    }
    const conversationPublicId =
      asString(eventRecord.conversationPublicId) ??
      asString(record.conversationPublicId) ??
      null;

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      conversationPublicId: conversationPublicId ?? undefined,
      event: {
        hypothesisPublicId,
        projectPublicId: eventProjectPublicId,
        conversationPublicId,
      },
    };
  }

  if (name === "experimentChangedEvent") {
    const experimentPublicId =
      asString(eventRecord.experimentPublicId) ?? entityPublicId;
    const eventProjectPublicId =
      asString(eventRecord.projectPublicId) ?? projectPublicId;
    if (!experimentPublicId || !eventProjectPublicId) {
      return null;
    }
    const conversationPublicId =
      asString(eventRecord.conversationPublicId) ??
      asString(record.conversationPublicId) ??
      null;

    return {
      name,
      entityType,
      entityPublicId,
      userPublicId: asString(record.userPublicId),
      projectPublicId: eventProjectPublicId,
      conversationPublicId: conversationPublicId ?? undefined,
      event: {
        experimentPublicId,
        projectPublicId: eventProjectPublicId,
        conversationPublicId,
      },
    };
  }

  return null;
}

function asConversationMessageChangeKind(
  value: unknown,
): ConversationMessageChangeKind | undefined {
  return typeof value === "string" &&
    CONVERSATION_MESSAGE_CHANGE_KINDS.includes(
      value as ConversationMessageChangeKind,
    )
    ? (value as ConversationMessageChangeKind)
    : undefined;
}
